/**
 * Session lifecycle states for one auth domain (user or admin).
 *
 * See documentation/architecture/session-management-redesign.md for
 * state-diagram and transition rules.
 *
 * String values back the union so the existing `'alive'` / `'dead'` shape
 * remains valid (e.g. in test fixtures and persisted snapshots). Property
 * names are the ergonomic surface for emitter / comparison sites.
 */
export const SessionState = {
  /** initial; haven't probed yet */
  Unknown: 'unknown',
  /** probe in flight */
  Probing: 'probing',
  /** probe confirmed no session; render login peacefully */
  Anonymous: 'anonymous',
  /** valid session; requests can fire */
  Alive: 'alive',
  /** refresh in flight; requests should queue */
  Refreshing: 'refreshing',
  /** session existed and is gone; show redirect modal */
  Dead: 'dead',
} as const;

export type SessionState = (typeof SessionState)[keyof typeof SessionState];

/** States that awaitReady() resolves to. Transient states are excluded. */
export type TerminalState = Extract<
  SessionState,
  typeof SessionState.Anonymous | typeof SessionState.Alive | typeof SessionState.Dead
>;

/**
 * Type guard for terminal states. Centralizes the membership check so a
 * future addition to the union doesn't require updating two call sites
 * in lockstep.
 */
export function isTerminal(state: SessionState): state is TerminalState {
  return (
    state === SessionState.Alive || state === SessionState.Anonymous || state === SessionState.Dead
  );
}

/**
 * True for the states in which an authenticated session exists and the user
 * should be treated as logged in. `Refreshing` is included deliberately: it is
 * an Alive session renewing its access token (a sub-second hop on the proactive
 * timer, a concurrent-401 recovery, or visibility-regain), NOT a logged-out
 * state.
 *
 * Route guards and every `isAuthenticated` derivation MUST use this rather than
 * comparing against `SessionState.Alive` directly. Excluding `Refreshing` makes
 * `isAuthenticated` read false for the duration of a proactive refresh, so a
 * route guard re-evaluating during that window redirects an actively-working
 * user to /login on a healthy, just-refreshed session (200 on the refresh, no
 * 401 anywhere). That was the recurring spurious-logout class — see CU-868kyhnm9
 * and documentation/architecture/session-management-redesign.md. Keep the
 * predicate here so AuthContext, AdminAuthContext, AuthErrorBoundary, and
 * ApiClient share one source of truth instead of hand-rolling `Alive ||
 * Refreshing` (or forgetting the `Refreshing` half) at each site.
 */
export function isAuthenticatedState(state: SessionState): boolean {
  return state === SessionState.Alive || state === SessionState.Refreshing;
}

/** Configuration passed to a SessionManager instance. One config per domain. */
export interface SessionConfig {
  /** Domain identifier — drives directive routing and login redirects. */
  domain: 'user' | 'admin';
  /** Endpoint that returns 200 with session info if alive, 401 otherwise. */
  probeEndpoint: string;
  /** Endpoint that mints a new access token from a valid refresh cookie. */
  refreshEndpoint: string;
  /** Where to send the user when the session dies. */
  loginRedirectUrl: string;
  /**
   * Optional predicate to determine if a 2xx probe response actually indicates
   * an authenticated session. When omitted, probe() treats any 2xx as `alive`.
   *
   * Use this for probe endpoints that return 200 with a body field
   * (e.g. `{authenticated: false}`) instead of 401 when not authenticated.
   * Returning `false` from this predicate transitions state to `anonymous`
   * even on a 2xx response.
   */
  isAliveFromBody?: (body: unknown) => boolean;
}
